import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { CircleCheck, CircleHelp, CircleX, TriangleAlert, type LucideIcon } from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────

type ModalVariant = "confirm" | "success" | "error" | "warning";

interface ModalState {
  open: boolean;
  variant: ModalVariant;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ModalContextValue {
  showConfirm: (title: string, message: string) => Promise<boolean>;
  showSuccess: (title: string, message: string) => void;
  showError: (title: string, message: string) => void;
  showWarning: (title: string, message: string) => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useModal must be used inside <ModalProvider>");
  return ctx;
}

// ── Styling ────────────────────────────────────────────────────────

const VARIANT_STYLES: Record<ModalVariant, { icon: LucideIcon; iconBg: string; btnClass: string }> =
  {
    confirm: {
      icon: CircleHelp,
      iconBg: "bg-warning/15 text-warning",
      btnClass: "bg-accent hover:bg-accent-light text-bg",
    },
    success: {
      icon: CircleCheck,
      iconBg: "bg-positive/15 text-positive",
      btnClass: "bg-positive hover:bg-positive/80 text-bg",
    },
    error: {
      icon: CircleX,
      iconBg: "bg-negative/15 text-negative",
      btnClass: "bg-negative hover:bg-negative/80 text-white",
    },
    warning: {
      icon: TriangleAlert,
      iconBg: "bg-warning/15 text-warning",
      btnClass: "bg-warning hover:bg-warning/80 text-white",
    },
  };

// ── Provider ───────────────────────────────────────────────────────

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modal, setModal] = useState<ModalState>({
    open: false,
    variant: "confirm",
    title: "",
    message: "",
  });

  // Resolve ref for confirm dialogs
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const close = useCallback((result: boolean) => {
    setModal((prev) => ({ ...prev, open: false }));
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  }, []);

  const showConfirm = useCallback((title: string, message: string): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setModal({
        open: true,
        variant: "confirm",
        title,
        message,
        confirmLabel: "Confirm",
        cancelLabel: "Cancel",
      });
    });
  }, []);

  const showAlert = useCallback((variant: ModalVariant, title: string, message: string) => {
    resolveRef.current = null;
    setModal({ open: true, variant, title, message, confirmLabel: "OK" });
  }, []);

  const showSuccess = useCallback(
    (title: string, message: string) => showAlert("success", title, message),
    [showAlert],
  );
  const showError = useCallback(
    (title: string, message: string) => showAlert("error", title, message),
    [showAlert],
  );
  const showWarning = useCallback(
    (title: string, message: string) => showAlert("warning", title, message),
    [showAlert],
  );

  const style = VARIANT_STYLES[modal.variant];

  return (
    <ModalContext.Provider value={{ showConfirm, showSuccess, showError, showWarning }}>
      {children}

      {modal.open ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => close(false)}
            role="presentation"
          />

          {/* Dialog */}
          <div className="relative mx-4 w-full max-w-sm rounded-2xl border border-border bg-surface p-6 shadow-2xl">
            {/* Icon */}
            <div className="mb-4 flex justify-center">
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full ${style.iconBg}`}
              >
                <style.icon size={22} strokeWidth={2} aria-hidden="true" />
              </div>
            </div>

            {/* Text */}
            <h2 className="text-center text-[16px] font-semibold text-text">{modal.title}</h2>
            <p className="mt-2 text-center text-[13px] leading-relaxed text-text-muted">
              {modal.message}
            </p>

            {/* Buttons */}
            <div className="mt-6 flex gap-3">
              {modal.cancelLabel ? (
                <button
                  type="button"
                  onClick={() => close(false)}
                  className="flex-1 rounded-lg border border-border px-4 py-2.5 text-[13px] font-semibold text-text-muted transition-colors hover:bg-surface-high hover:text-text"
                >
                  {modal.cancelLabel}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => close(true)}
                className={`flex-1 rounded-lg px-4 py-2.5 text-[13px] font-semibold transition-colors ${style.btnClass}`}
              >
                {modal.confirmLabel ?? "OK"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ModalContext.Provider>
  );
}
